'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:quoteMediaSymbolLookup');
const { symbolLookup } = require('./symbolLookup.js');
const { waitForElement } = require('../../lib/util');

getDependencyClass('phoenix-page').then((pageClass) => {
    class PhoenixQuoteMediaSymbolLookup extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return ['phoenix:overlay:visible'];
        }

        broadcastedEventCallback(name, args = {}) {
            const eventValue = args.detail && args.detail.value;
            // Once the 'offcanvassearch' overlay is visible, focus on the input and put cursor at end.
            if (name === 'phoenix:overlay:visible' && eventValue === 'offcanvassearch') {
                const searchInput = document.querySelector('#m-off-canvas-search--search-input');
                searchInput.focus({ preventScroll: true });
                searchInput.setSelectionRange(1000, 1000);
            }
        }

        connectedCallback() {
            super.connectedCallback();
            this.inOffCanvasSearch = !!this.getAttr('in-off-canvas-search');
            this.baseUrl = this.config?.baseUrl ?? '/';
            // If no searchFuncName is provided, a randomized value will be postfixed to prevent clashes when
            // more than one QuoteMediaSymbolLookup components exist per page.
            this.searchFuncName =
                this.config?.searchFuncName ??
                `quoteMediaSearch_${Date.now() + '_' + Math.floor(Math.random() * 100000)}`;
            window[this.searchFuncName] = (searchResult) => {
                if (!searchResult) {
                    return;
                }

                if (searchResult.event === 'go' || searchResult.event === 'enter') {
                    const searchQuery = encodeURIComponent(searchResult.symbol);
                    window.location.href = `/search?query=${searchQuery}`;
                }

                if (searchResult.marketCap === undefined || !searchResult.symbol) {
                    return;
                }

                const symbol = encodeURIComponent(searchResult.symbol);
                const quoteUrl = `${this.baseUrl}quote/${symbol}`;
                if (this.baseUrl === '/') {
                    window.location.href = quoteUrl;
                } else {
                    window.open(quoteUrl);
                    this.dispatchEvent(new Event('refresh-search-results', { bubbles: false }));
                }
            };
            this._render();
            this._addPropagatedEventListeners();
            // Assume the user is attempting to close the search results.
            document.addEventListener('click', this._closeSearchResults);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._removeListener();
            delete window[this.searchFuncName];
        }

        _closeSearchResults = (event) => {
            // The QMod backdrop only renders when search results exist.
            const elements = this.getElementsByClassName('qmod-ui-results-backdrop');
            if (!elements || elements.length === 0 || !elements[0]) {
                return;
            }

            // Ignore click if user clicks outside of QuoteMediaSymbolLookup.
            const qModBackdrop = elements[0];
            if (this.contains(event.target)) {
                return;
            }

            // Simulate click on QuoteMedia div; this causes search results to close.
            qModBackdrop.click();
        };

        _removeListener() {
            document.removeEventListener('click', this._closeSearchResults);
            window.removeEventListener('pageshow', this._refreshSearchResults);
            this.removeListener('refresh-search-results', this._refreshSearchResults);
        }

        /**
         * This function adds the event listeners to the OffCanvasInput that propagate the
         * events to the QMod search input. On input or click to the OffCanvasSearch input,
         * events are also sent to the QMod search input to query for data.
         */
        _addPropagatedEventListeners() {
            if (!this.inOffCanvasSearch) {
                return;
            }

            const offCanvasSearchInput = document.querySelector('#m-off-canvas-search--search-input');
            if (!offCanvasSearchInput) {
                return;
            }

            // Wait for the QMod search input to load.
            waitForElement(this, '.m-quote-media-symbol-lookup--hidden-input').then((element) => {
                // Mirror these events from OffCanvasSearch to the QMod search input.
                ['click', 'input'].forEach((eventType) => {
                    offCanvasSearchInput.addEventListener(eventType, (e) => {
                        this._dispatchEventWithValue(eventType, element, e.target?.value);
                    });
                });

                // Dispatch an event to trigger search results on page back.
                this._refreshSearchResults = () => {
                    try {
                        this._dispatchEventWithValue('input', element, offCanvasSearchInput.value);
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error('quoteMediaSymbolLookup/main.js could not find inputs.');
                    }
                };
                window.addEventListener('pageshow', this._refreshSearchResults);
                this.addEventListener('refresh-search-results', this._refreshSearchResults);

                // Dispatch an event to trigger search results on QMod load.
                this._dispatchEventWithValue('input', element, offCanvasSearchInput.value);
            });
        }

        /**
         * Set the value of an element and dispatch the event.
         * @param eventType - The type of the event created.
         * @param element - The element that will receive the event.
         * @param value - The value the event will hold.
         */
        _dispatchEventWithValue(eventType, element, value) {
            if (!element) {
                return;
            }

            element.value = value;
            element.dispatchEvent(new Event(eventType, { bubbles: false }));
        }

        /**
         * This function enables the use of a gradient fill on the OffCanvasSearch input.
         * @returns {string}
         * @private
         */
        _svgGradientTemplate() {
            return `
                <svg style='width:0;height:0;position:absolute;' aria-hidden='true' focusable='false'>
                    <linearGradient id='m-quote-media-symbol-lookup--svg-gradient' x2='1' y2='1'>
                    <stop offset='0%' stop-color='#026289' />
                    <stop offset='100%' stop-color='#61408D' />
                    </linearGradient>
                </svg>

            `;
        }

        _render() {
            const qModParams = {
                callbackfunc: this.searchFuncName,
                callbackurl: 'quote/',
                lang: 'en',
                search: 'symbol',
                symbology: 'US',
            };
            const customTemplate = `<script type='text/template'>${symbolLookup()}</script>`;

            this.innerHTML = `
                ${this._svgGradientTemplate()}
                <div
                    data-qmod-tool='smartsymbollookup'
                    data-qmod-params='${JSON.stringify(qModParams)}'
                    class='qtool'
                >
                    ${this.inOffCanvasSearch ? customTemplate : ''}
                </div>
            `;
        }
    }
    window.customElements.define('phoenix-quote-media-symbol-lookup', PhoenixQuoteMediaSymbolLookup);
    measure.end();
});
